import React, { useRef, useState, useEffect } from 'react';
import { ArrowRight, ChevronUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { FOUNDER } from '../data/mock';

// Scroll to Top Button
const ScrollToTop = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const toggleVisibility = () => setIsVisible(window.scrollY > 400);
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  return (
    <button
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      className={`scroll-to-top ${isVisible ? 'visible' : ''}`}
      aria-label="Scroll to top"
    >
      <ChevronUp size={20} />
    </button>
  );
};

const PerspectivePage = () => {
  const philosophyRef = useRef(null);
  const worldviewRef = useRef(null);
  const [philosophyVisible, setPhilosophyVisible] = useState(false);
  const [worldviewVisible, setWorldviewVisible] = useState(false);

  useEffect(() => {
    const observerOptions = { threshold: 0.2 };

    const philosophyObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setPhilosophyVisible(true);
    }, observerOptions);

    const worldviewObserver = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) setWorldviewVisible(true);
    }, observerOptions);

    if (philosophyRef.current) philosophyObserver.observe(philosophyRef.current);
    if (worldviewRef.current) worldviewObserver.observe(worldviewRef.current);

    return () => {
      philosophyObserver.disconnect();
      worldviewObserver.disconnect();
    };
  }, []);

  return (
    <main className="pt-24">
      {/* Hero Section - Matching Home Page Design */}
      <section className="py-12 md:py-16 lg:py-20 px-6 md:px-12 lg:px-16 section-champagne relative">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-center">
            {/* Image Column - Same as Home Page */}
            <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
              <div className="founder-image-wrapper">
                <img
                  src={FOUNDER.image}
                  alt={FOUNDER.name}
                  className="w-full aspect-[3/4] object-cover"
                />
              </div>
            </div>

            {/* Text Column - Same as Home Page */}
            <div className="animate-fade-in-up opacity-0" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
              <h1 className="display-md mb-3">{FOUNDER.name}</h1>
              <p 
                className="body-md mb-10"
                style={{ color: 'var(--velora-gold-muted)' }}
              >
                {FOUNDER.title}
              </p>
              <div className="divider-gold" />
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section ref={philosophyRef} className="pt-6 pb-12 md:pt-20 md:pb-24 px-6 md:px-12 lg:px-16 section-cream relative">
        <div className="absolute top-0 left-0 w-1/3 h-full bg-gradient-to-r from-velora-gold/[0.03] to-transparent" />

        <div className="container-luxury relative z-10">
          <div className="max-w-4xl mx-auto">
            <span 
              className={`label-sm mb-6 md:mb-10 block transition-all duration-1000 ${
                philosophyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ color: 'var(--velora-gold)' }}
            >
              Philosophy
            </span>
            <blockquote 
              className={`display-md italic mb-10 md:mb-14 transition-all duration-1000 ${
                philosophyVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'
              }`}
              style={{ color: 'var(--velora-green-deep)', transitionDelay: '200ms' }}
            >
              "I don't believe brands win by doing more. They win by seeing clearly — before others do. 
              Velora was built on that principle."
            </blockquote>
            <div 
              className={`divider-gold transition-all duration-1000 ${
                philosophyVisible ? 'opacity-100 scale-x-100' : 'opacity-0 scale-x-0'
              }`}
              style={{ transitionDelay: '400ms', transformOrigin: 'left' }}
            />
          </div>
        </div>
      </section>

      {/* Worldview Section */}
      <section ref={worldviewRef} className="section-padding section-ivory">
        <div className="container-luxury">
          <div className="max-w-4xl mx-auto">
            <span 
              className={`label-sm mb-10 block transition-all duration-1000 ${
                worldviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
              }`}
              style={{ color: 'var(--velora-gold)' }}
            >
              Worldview
            </span>
            <div className="space-y-0">
              {FOUNDER.worldview.map((belief, index) => (
                <div
                  key={index}
                  className={`flex items-start space-x-10 py-10 border-b transition-all duration-1000 last:border-0 ${
                    worldviewVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                  }`}
                  style={{ 
                    borderColor: 'rgba(196, 169, 98, 0.15)',
                    transitionDelay: `${200 + index * 150}ms`
                  }}
                >
                  <span
                    className="label-sm mt-2 flex-shrink-0"
                    style={{ color: 'var(--velora-gold)' }}
                  >
                    0{index + 1}
                  </span>
                  <p className="body-editorial text-xl" style={{ color: 'var(--velora-green-deep)' }}>
                    {belief}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Authority Section - Founder Statement */}
      <section className="py-12 md:py-20 section-green relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-velora-gold/[0.03] to-transparent" />
          <div className="absolute bottom-0 left-0 w-1/3 h-1/2 bg-gradient-to-t from-velora-gold/[0.02] to-transparent" />
        </div>

        <div className="container-luxury relative z-10">
          <div className="max-w-4xl mx-auto text-center px-4">
            <h2 
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl italic leading-relaxed md:leading-relaxed"
              style={{ 
                color: 'var(--velora-ivory)',
                fontFamily: "'Cormorant Garamond', serif",
                fontWeight: 400,
                letterSpacing: '-0.01em'
              }}
            >
              "Great brands are not discovered.<br className="hidden sm:block" />
              <span className="sm:hidden"> </span>They are recognised — because they see<br className="hidden md:block" />
              <span className="md:hidden"> </span>the world before it changes."
            </h2>
          </div>
        </div>
      </section>

      {/* Pattern Recognition Section */}
      <section className="section-padding section-cream">
        <div className="container-luxury">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-20 items-start">
            <div>
              <span className="label-sm mb-8 block" style={{ color: 'var(--velora-gold)' }}>Pattern Recognition</span>
              <h2 className="display-md mb-10">Seeing what others don't.</h2>
              <div className="divider-gold" />
            </div>
            <div>
              <p className="body-editorial text-lg mb-8">
                The most valuable skill in branding isn't creativity — it's pattern recognition.
                The ability to see market shifts before they happen. To understand cultural
                movements before they peak. To position brands where competitors haven't looked.
              </p>
              <p className="body-editorial text-lg mb-8">
                This is what Velora Marketing brings to every engagement. Not just execution, but
                foresight. Not just strategy, but vision.
              </p>
              <p className="body-editorial text-lg italic" style={{ color: 'var(--velora-green-deep)' }}>
                We don't follow markets. We anticipate them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding section-ivory text-center">
        <div className="container-luxury max-w-3xl">
          <h2 className="display-md mb-10">Ready to see differently?</h2>
          <p className="body-editorial text-lg mb-14" style={{ color: 'var(--velora-charcoal-light)' }}>
            The conversation starts with understanding where you are and where you could be.
          </p>
          <a 
            href="https://wa.me/918208377252?text=Hello%20Ashwini%2C%0AI%20came%20across%20Velora%20and%20would%20like%20to%20request%20access%20to%20explore%20a%20potential%20collaboration."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-access btn-access-dark group"
          >
            <span className="relative z-10">Begin The Conversation</span>
            <ArrowRight size={14} className="ml-3 relative z-10 transition-transform duration-500 group-hover:translate-x-1" />
          </a>
        </div>
      </section>

      <ScrollToTop />
    </main>
  );
};

export default PerspectivePage;
